import React from "react";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Textarea } from "src/components/ui/textarea";
import { useMe } from "src/context/MeContext";
import { getProductName } from "src/utils/brandTheme";

import { APPROVAL_TYPE_LABELS, type ApprovalTypeOption, type WizardState } from "../types";

interface Step3ConfigureProps {
  state: WizardState;
  onUpdate: (updates: Partial<WizardState>) => void;
}

export function Step3Configure({ state, onUpdate }: Step3ConfigureProps) {
  const me = useMe();
  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";

  return (
    <div className="space-y-8">
      {/* Description */}
      <div>
        <Label htmlFor="description" className="text-muted-foreground text-sm font-medium">
          Description
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <p className="text-sm text-muted-foreground mb-3">
          Describe what this pipeline does and when {getProductName(brandTheme)} should call this pipeline.
        </p>
        <Textarea
          id="description"
          placeholder={
            'e.g. "Deploys the latest version of the application to the production environment. Should be called after a successful build and when the user explicitly requests a deployment."'
          }
          rows={4}
          value={state.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          className="font-mono text-sm bg-card border-border w-full min-h-[100px]"
        />
      </div>

      {/* Approval Type */}
      <div>
        <Label htmlFor="approval" className="text-muted-foreground text-sm font-medium">
          Approval Type
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <p className="text-sm text-muted-foreground mb-3">
          Specify if this capability requires human approval before execution.
        </p>
        <Select value={state.approvalType} onValueChange={(v) => onUpdate({ approvalType: v as ApprovalTypeOption })}>
          <SelectTrigger className="bg-card border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(APPROVAL_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
